import { BrowserUtils } from "./BrowserUtils";
import { ExecutorUtils } from "@emed-labs/platform-utility-common-utils";
import { LoggerContext } from "@emed-labs/platform-utility-logger-context";
import { nanoid } from "nanoid";

interface PersistenceData {
	time: number;
	expires?: number;
	data: any;
}

export class LocalSessionStorage {
	/**
	 * storage namespace
	 * @private
	 */
	private readonly _ns: string;
	/**
	 * helper to compute the full storage key
	 * @private
	 * @param key
	 */
	private readonly _computeKey = (key: string) => `${this._ns}/${key}`;
	private readonly _locks: Record<string, string> = {};
	private readonly _lockResponses: Record<string, string> = {};
	private readonly _lockHandler = (event: StorageEvent) => {
		if (event.key?.indexOf("lock_request/") === 0) {
			const key = event.key.replace("lock_request/", "");
			if (key && this._locks[key]) {
				window.localStorage.setItem(`lock_response/${key}`, this._locks[key]);
				window.localStorage.removeItem(`lock_response/${key}`);
			}
		} else if (event.key?.indexOf("lock_response/") === 0) {
			const key = event.key.replace("lock_response/", "");
			const value = event.newValue;
			if (key) {
				this._lockResponses[key] = value;
			}
		}
	};
	private _lockHandlerInit: boolean = false;
	constructor(ns: string) {
		this._ns = ns;
	}
	public async lock(key: string, global?: boolean): Promise<boolean> {
		const full_key = global ? key : this._computeKey(key);
		/**
		 * allow for re-entrant locks
		 */
		if (this._locks[full_key]) {
			return true;
		}
		delete this._lockResponses[full_key];
		/**
		 * initialize the event handler
		 */
		if (!this._lockHandlerInit) {
			window.addEventListener("storage", this._lockHandler, false);
			this._lockHandlerInit = true;
		}
		/**
		 * lock value
		 */
		const request = nanoid(32);
		/**
		 * write the lock request
		 */
		window.localStorage.setItem(`lock_request/${full_key}`, request);
		window.localStorage.removeItem(`lock_request/${full_key}`);
		/**
		 * wait a period of time for other instances to respond
		 */
		return ExecutorUtils.schedule(() => {
			if (this._lockResponses[full_key] !== request) {
				return false;
			} else {
				// If nobody responds to owning this lock, then go ahead and consider it our own.
				this._locks[full_key] = request;
				return true;
			}
		}, 250).promise();
	}
	public unlock(key: string, global?: boolean): void {
		if (global) {
			delete this._locks[key];
		} else {
			delete this._locks[this._computeKey(key)];
		}
	}
	public put(key: string, data: any, ttl?: number): any {
		if (!BrowserUtils.isBrowser()) {
			throw new Error("Cannot access LocalStorage");
		}
		const now = Date.now();
		const box: PersistenceData = {
			time: now,
			data: data
		};
		if (ttl) {
			box.expires = now + ttl * 1000;
		}
		let current = this.get(key);
		window.localStorage.setItem(this._computeKey(key), JSON.stringify(box));
		return current;
	}
	public get(key: string): any {
		if (!BrowserUtils.isBrowser()) {
			throw new Error("Cannot access LocalStorage");
		}
		try {
			const box: PersistenceData = JSON.parse(window.localStorage.getItem(this._computeKey(key)));
			if (box) {
				if (box.expires && Date.now() >= box.expires) {
					this.clear(key);
					return null;
				}
				return box.data;
			} else {
				return undefined;
			}
		} catch (e) {
			PersistenceUtilsLOGGER.error("get", `Error when reading object from LocalStorage: ${e.message}`, e);
		}
	}
	public clear(key: string): any {
		if (!BrowserUtils.isBrowser()) {
			throw new Error("Cannot access LocalStorage");
		}
		window.localStorage.removeItem(this._computeKey(key));
	}
}

const PersistenceUtilsLOGGER = LoggerContext.create("utils.PersistenceUtils");

export class PersistenceUtils {
	private static _lockHandlerInitialized: boolean = false;
	private static _locksOwned: { [lockKey: string]: boolean } = {};
	private static _lockResponses: { [lockKey: string]: string } = {};

	private static _lockHandler(event: StorageEvent) {
		if (event.key?.indexOf("lock_request/") === 0) {
			const lockKey = event.newValue;
			if (lockKey && PersistenceUtils._locksOwned[lockKey]) {
				window.localStorage.setItem(`lock_response/${lockKey}`, lockKey);
				window.localStorage.removeItem(`lock_response/${lockKey}`);
			}
		} else if (event.key?.indexOf("lock_response/") === 0) {
			const lockKey = event.newValue;
			if (lockKey) {
				PersistenceUtils._lockResponses[lockKey] = lockKey;
			}
		}
	}

	/**
	 * Places a lock/mutex for a certain key. The lock occurs across all browser windows that share the same origin. Returns true if the lock is successful, false if the lock is already locked.
	 * @param key
	 */
	static async placeLock(key: string): Promise<boolean> {
		if (!BrowserUtils.isBrowser()) {
			return true;
		}

		if (this._locksOwned[key] === true) {
			return true;
		}

		PersistenceUtils._lockResponses[key] = null;

		// Send a lock request.
		window.localStorage.setItem(`lock_request/${key}`, key);
		window.localStorage.removeItem(`lock_request/${key}`);

		if (!PersistenceUtils._lockHandlerInitialized) {
			window.addEventListener("storage", PersistenceUtils._lockHandler, false);
			PersistenceUtils._lockHandlerInitialized = true;
		}

		return new Promise((resolve) => {
			// Wait a few milliseconds to see if another window responds to this lock request.
			setTimeout(() => {
				if (PersistenceUtils._lockResponses[key] === key) {
					resolve(false);
				} else {
					// It nobody responds to owning this lock, then go ahead and consider it our own.
					this._locksOwned[key] = true;
					resolve(true);
				}
			}, 250);
		});
	}

	static setData(key: string, data: any, expiresInSeconds?: number) {
		const now = Date.now();

		const pd: PersistenceData = {
			time: now,
			data: data
		};

		if (expiresInSeconds) {
			pd.expires = now + expiresInSeconds * 1000;
		}

		window.localStorage.setItem(key, JSON.stringify(pd));
	}

	static deleteData(key: string) {
		window.localStorage.removeItem(key);
	}

	static getData<T = any>(key: string): T {
		try {
			const pd: PersistenceData = JSON.parse(window.localStorage.getItem(key));

			if (pd.expires && Date.now() >= pd.expires) {
				PersistenceUtils.deleteData(key);
				return;
			}

			return pd.data;
		} catch (e) {}
	}
}
